import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { Address, CreateAddressDto, UpdateAddressDto } from '@/lib/types';

export function useAddresses() {
  return useQuery({
    queryKey: ['/addresses'],
    queryFn: async (): Promise<Address[]> => {
      const token = apiClient.getToken();
      if (!token) return [];
      
      const response = await apiClient.get('/addresses');
      if (!response.ok) {
        if (response.status === 401) return [];
        throw new Error('Failed to fetch addresses');
      }
      return response.json();
    },
    enabled: !!apiClient.getToken(),
  });
}

export function useCreateAddress() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: CreateAddressDto): Promise<Address> => {
      const response = await apiClient.post('/addresses', data);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create address');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/addresses'] });
    },
  });
}

export function useUpdateAddress() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateAddressDto }): Promise<Address> => {
      const response = await apiClient.put(`/addresses/${id}`, data);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update address');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/addresses'] });
    },
  });
}

export function useDeleteAddress() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const response = await apiClient.delete(`/addresses/${id}`);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete address');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/addresses'] });
    },
  });
}

export function useSetDefaultAddress() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string): Promise<Address> => {
      const response = await apiClient.put(`/addresses/${id}/default`);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to set default address');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/addresses'] });
    },
  });
}
